const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,600&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap');

:root {
  --gold: #D4A853;
  --gold-light: #F0DFB4;
  --rose: #C77D8A;
  --rose-light: #F2D4D9;
  --plum: #2C1A2E;
  --plum-mid: #4A2D4F;
  --night: #1A0F1C;
  --blush-tint: #F6F0F2;
  --blush-glow: #F5E8EA;
  --text-primary: #2C1A2E;
  --text-secondary: #6B5570;
  --text-light: #9A8A9E;
  --white: #FFFFFF;
  --glass: rgba(255,255,255,0.08);
  --glass-border: rgba(255,255,255,0.12);
  --shadow-sm: 0 2px 8px rgba(44,26,46,0.06);
  --shadow-md: 0 8px 30px rgba(44,26,46,0.10);
  --shadow-lg: 0 20px 60px rgba(44,26,46,0.14);
  --shadow-glow: 0 0 40px rgba(212,168,83,0.15);
  --radius-sm: 8px;
  --radius-md: 14px;
  --radius-lg: 22px;
  --radius-xl: 32px;
  --font-display: 'Cormorant Garamond', Georgia, serif;
  --font-body: 'DM Sans', system-ui, sans-serif;
  --nav-h: 72px;
  --max-w: 1200px;
  --section-py: clamp(64px, 10vw, 120px);
}

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

html { scroll-behavior: smooth; -webkit-font-smoothing: antialiased; }
body { font-family: var(--font-body); color: var(--text-primary); background: var(--blush-tint); line-height: 1.65; overflow-x: hidden; }

::selection { background: var(--gold-light); color: var(--plum); }

/* ── Scrollbar ── */
::-webkit-scrollbar { width: 8px; }
::-webkit-scrollbar-track { background: var(--blush-tint); }
::-webkit-scrollbar-thumb { background: var(--gold); border-radius: 99px; }

/* ── Utility ── */
.sb-container { width: 100%; max-width: var(--max-w); margin: 0 auto; padding: 0 clamp(20px, 4vw, 48px); }
.sb-section { padding: var(--section-py) 0; }
.sb-gold-text { color: var(--gold); }
.sb-rose-text { color: var(--rose); }

/* ── Typography ── */
h1, h2, h3, h4 { font-family: var(--font-display); font-weight: 600; line-height: 1.2; letter-spacing: -0.01em; }
h1 { font-size: clamp(2.4rem, 5.5vw, 4.2rem); }
h2 { font-size: clamp(1.8rem, 3.5vw, 2.8rem); }
h3 { font-size: clamp(1.2rem, 2vw, 1.6rem); }
p { font-size: clamp(0.95rem, 1.1vw, 1.1rem); }

/* ── Buttons ── */
.sb-btn {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 14px 32px; border-radius: 99px; font-family: var(--font-body);
  font-weight: 600; font-size: 0.95rem; letter-spacing: 0.02em;
  cursor: pointer; border: none; text-decoration: none;
  transition: all 0.35s cubic-bezier(.4,0,.2,1); position: relative; overflow: hidden;
}
.sb-btn-primary {
  background: linear-gradient(135deg, var(--gold), #E0B95C);
  color: var(--plum); box-shadow: var(--shadow-glow);
}
.sb-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 0 50px rgba(212,168,83,0.3); }
.sb-btn-secondary {
  background: transparent; color: var(--white);
  border: 1.5px solid rgba(255,255,255,0.4);
}
.sb-btn-secondary:hover { background: rgba(255,255,255,0.1); border-color: var(--gold); color: var(--gold); }
.sb-btn-dark {
  background: var(--plum); color: var(--gold);
}
.sb-btn-dark:hover { background: var(--plum-mid); transform: translateY(-2px); box-shadow: var(--shadow-md); }
.sb-btn-outline {
  background: transparent; color: var(--plum); border: 1.5px solid var(--plum);
}
.sb-btn-outline:hover { background: var(--plum); color: var(--gold); }

/* ── Noise texture overlay ── */
.sb-noise::before {
  content: ''; position: absolute; inset: 0; z-index: 1;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
  pointer-events: none;
}

/* ── Decorative divider ── */
.sb-divider {
  width: 60px; height: 2px;
  background: linear-gradient(90deg, var(--gold), var(--rose));
  border-radius: 99px;
}

/* ── Keyframes ── */
@keyframes sb-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
@keyframes sb-shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
@keyframes sb-fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
@keyframes sb-pulse { 0%,100%{opacity:0.6} 50%{opacity:1} }

/* ── Form ── */
.sb-input {
  width: 100%; padding: 14px 18px; border: 1.5px solid #E0D6E2;
  border-radius: var(--radius-sm); font-family: var(--font-body); font-size: 0.95rem;
  background: var(--white); color: var(--text-primary);
  transition: border-color 0.3s, box-shadow 0.3s; outline: none;
}
.sb-input:focus { border-color: var(--gold); box-shadow: 0 0 0 3px rgba(212,168,83,0.12); }
.sb-input::placeholder { color: var(--text-light); }
textarea.sb-input { resize: vertical; min-height: 120px; }
/* ── Date / Time inputs — force left-align on iOS Safari ── */
input[type="date"].sb-input-datetime,
input[type="time"].sb-input-datetime {
  text-align: left;
  -webkit-text-align: left;
  display: block;
}
/* Safari renders an internal flex container; target it to left-align the value */
input[type="date"].sb-input-datetime::-webkit-date-and-time-value,
input[type="time"].sb-input-datetime::-webkit-date-and-time-value {
  text-align: left;
  margin: 0;
}

input[type="number"].sb-input::-webkit-inner-spin-button,
input[type="number"].sb-input::-webkit-outer-spin-button {
  opacity: 1;
  height: 28px;
  cursor: pointer;
}
input[type="number"].sb-input { -moz-appearance: number-input; }

/* ── Nav Mobile ── */
@media (max-width: 768px) {
  .sb-nav-links { display: none !important; }
  .sb-mobile-toggle { display: flex !important; }
}
`;

export default STYLES;
